import DanaOrderUploadStep1File from "@dana/Components/DanaOrderUpload/DanaOrderUploadStep1File";
import DanaOrderUploadStep1Paste from "@dana/Components/DanaOrderUpload/DanaOrderUploadStep1Paste";
import DanaOrderUploadStep2Validate from "@dana/Components/DanaOrderUpload/DanaOrderUploadStep2Validate";
import DanaOrderUploadStep3Confirm from "@dana/Components/DanaOrderUpload/DanaOrderUploadStep3Confirm";
import {
    checkQtyError,
    checkDateError,
    DanaOrderUploadFileUploadStyles,
    EditableRow,
    ImportTab,
    InputItem,
    parsePastedText,
    WizardStep,
    danaUploadErrorMessages,
} from "@dana/Components/DanaOrderUpload/DanaOrderUploadTypes";
import Modal, { ModalPresentationProps } from "@dana/MobiusOverrides/Modal/Modal";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights } from "@dana/MobiusOverrides/styles/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import batchLoadProducts, { UploadError } from "@insite/client-framework/Store/Components/OrderUpload/Handlers/BatchLoadProducts";
import cleanupUploadData from "@insite/client-framework/Store/Components/OrderUpload/Handlers/CleanupUploadData";
import processFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/ProcessFile";
import setAllowCancel from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetAllowCancel";
import setIsBadFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsBadFile";
import setIsUploading from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsUploading";
import setUploadCancelled from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetUploadCancelled";
import setUploadLimitExceeded from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetUploadLimitExceeded";
import addCartLineCollectionFromProducts from "@insite/client-framework/Store/Pages/OrderUpload/Handlers/AddCartLineCollectionFromProducts";
import translate from "@insite/client-framework/Translate";
import { ProductDto } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

import useIsEmea from "@dana/Common/Hooks/useIsEmea";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import siteMessage from "@insite/client-framework/SiteMessage";


export interface DanaOrderUploadProps {
    onUploadProducts?: (products: ProductDto[], onSuccess?: () => void) => void;
    templateUrl?: string;
    extendedStyles?: DanaOrderUploadFileUploadStyles;
    isOpen?: boolean;
    handleClose?: () => void;
    wishListId?: string;
    wishListName?: string;
}

const mapStateToProps = (state: ApplicationStateDana) => ({
    uploadedListItems: state.components.orderUpload.uploadedItems,
    isUploading: state.components.orderUpload.isUploading,
    products: state.components.orderUpload.products,
    rowErrors: state.components.orderUpload.rowErrors,
    isBadFile: state.components.orderUpload.isBadFile,
    uploadLimitExceeded: state.components.orderUpload.uploadLimitExceeded,
    productsProcessed: state.components.orderUpload.productsProcessed,
    allowCancel: state.components.orderUpload.allowCancel,
    uploadCancelled: state.components.orderUpload.uploadCancelled,
    isEmea: useIsEmea(state),
    uploadErrorMessages: danaUploadErrorMessages,
});

const mapDispatchToProps = {
    processFile,
    batchLoadProducts,
    cleanupUploadData,
    setAllowCancel,
    setIsBadFile,
    setIsUploading,
    setUploadCancelled,
    setUploadLimitExceeded,
    addCartLineCollectionFromProducts,
};

type ReduxProps = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
type Props = DanaOrderUploadProps & ReduxProps;

export interface DanaOrderUploadInnerStyle {
    modal?: ModalPresentationProps;
    modalHeadingWrapper?: InjectableCss;
    stepBarWrapper?: InjectableCss;
    stepBlock?: InjectableCss;
    stepCircle?: TypographyPresentationProps;
    stepLabel?: TypographyPresentationProps;
    backButton?: ButtonPresentationProps;
    nextButton?: ButtonPresentationProps;
    stepConnector?: TypographyPresentationProps;
    tabSectionWrapper?: InjectableCss;
    tabSection?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
}

export const danaOrderUploadInnerStyle: DanaOrderUploadInnerStyle = {
    modal: {
        size: 1200,
        cssOverrides: {
            modalTitle: css`
                padding: 0;
                color: ${themeBackgrounds.white};
                h3 {
                    color: ${themeBackgrounds.white};
                    margin-bottom: 20px;
                    display: flex;
                    span {
                        font-weight: ${fontWeights.regular};
                        font-size: ${fontSizes.medium};
                        opacity: 0.5;
                        margin-left: 8px;
                    }
                }
                
                [data-test-selector="modalCloseButton"] {
                    right: 28px;
                    top: 20px;
                    &,
                    &:hover,
                    &:focus {
                        background: transparent;
                        border: none;
                    }    
                    [class*="IconWrapper"] {
                        color: ${themeBackgrounds.white};
                        top: 0;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                &,
                                > div {
                                    border-radius: 8px 8px 0 0;
                                }
                            `
                        ],
                        "min",
                    )}
            `,
            modalBody: css`
                padding: 0;
                overflow-y: initial;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                            border-radius: 8px;
                        `
                        ],
                        "min",
                    )}
            `,
            modalFooter: css`
                padding: 16px 28px;
                background: ${themeBackgrounds.offWhite};
                border-top: 1px solid ${getColor("common.border")};
                display: flex;
                justify-content: center;
                flex-wrap: wrap;
                gap: 16px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                            border-radius: 0 0 8px 8px;
                            justify-content: space-between;
                        `
                        ],
                        "min",
                    )}
            `,
            modalContent: css`
                padding: 28px;
                overflow-y: auto;
                background: ${themeBackgrounds.white};
                textarea {
                    &,
                    &:focus {
                        height: 200px;
                        overflow: auto;
                    }
                }   

            `
        },
    },
    modalHeadingWrapper: {
        css: css`
            background: ${themeColors.primary.danaBlue};
            padding: 20px 28px 0;
            width: 100%;
        `,
    },
    stepBarWrapper: {
        css: css`
            display: flex;
            align-items: stretch;
        `
    },
    stepConnector: {
        css: css`
            width: 40px;
            height: 1px;
            background: ${themeColors.secondary.danaBrightBlue};
            align-self: center;
            margin-top: -18px;
            flex-shrink: 0;
            opacity: 0.5;
        `
    },
    stepBlock: {
        css: css`
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 12px 8px 0;
            position: relative;
            &::after {
                content: "";
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                height: 3px;
                border-radius: 2px 2px 0 0;
            }
        
            &.active {
                &:after {
                    background: ${themeBackgrounds.white};
                }
                .stepCircle {
                    opacity: 1;
                    background: ${themeBackgrounds.white};
                    color: ${themeColors.primary.richBlack};
                }
                .stepLabel {
                    opacity: 1;
                }
            }
            &.done {
                &:after {
                    background: ${themeColors.secondary.danaBrightBlue};
                }
                .stepLabel,
                .stepCircle {
                    opacity: 0.9;
                }
            }
        
        `
    },
    stepCircle: {
        size: fontSizes.small,
        weight: 700,
        css: css`
            width: 30px;
            height: 30px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 6px;
            color: ${themeBackgrounds.white};
            opacity: 0.5;
            background: ${themeColors.secondary.danaBrightBlue};
        `
    },
    stepLabel: {
        size: fontSizes.xSmall,
        weight: 700,
        transform: "uppercase",
        color: themeBackgrounds.white,
        css: css`
            letter-spacing: 0.5px;
            margin-bottom: 12px;
            opacity: 0.5;
            text-align: center;
        `
    },
    backButton: {
        variant: "secondary",
    },
    nextButton: {
        css: css`
            line-height: ${fontSizes.xxLarge};
            font-size: ${fontSizes.medium};
            font-weight: ${fontWeights.medium};
            min-height: 42px;
            align-items: center;
            gap: 4px;
            span {
                display: flex;
                align-items: center;
            }
        `
    },
    tabSectionWrapper: {
        css: css`
            display: flex;
            border-bottom: 2px solid ${getColor("common.border")};
            margin-bottom: 22px;
        `
    },
    tabSection: {
        css: css`
            margin-bottom: -2px;
            color: ${themeColors.primary.richBlack};
            
            &,
            &:hover,
            &:focus {
                padding: 10px 20px;
                cursor: pointer;
                border: none;
                background: none;
                border-bottom: 2px solid transparent;
                outline: none;
                &,
                [class*="TypographyStyle"] {
                    font-size: ${fontSizes.medium};
                    font-weight: ${fontWeights.semiBold}; 
                    color: ${themeColors.primary.richBlack};
                }
                &.active {
                    border-bottom: 2px solid ${themeColors.primary.danaBlue};
                    &,
                    [class*="TypographyStyle"] {
                        color: ${themeColors.primary.danaBlue};
                    }
                }
            }
        `
    },
    spinner: {
        css: css`
            width: 16px;
            height: 16px;
        `
    }
};


const DanaOrderUploadInner: FC<Props> = ({
    onUploadProducts,
    processFile,
    batchLoadProducts,
    cleanupUploadData,
    setAllowCancel,
    setIsBadFile,
    setIsUploading,
    setUploadCancelled,
    setUploadLimitExceeded,
    handleClose,
    templateUrl,
    extendedStyles,
    uploadedListItems,
    isUploading,
    products,
    rowErrors,
    isBadFile,
    uploadLimitExceeded,
    productsProcessed,
    isOpen,
    wishListId,
    wishListName,
    isEmea,
    uploadErrorMessages
}) => {

    const [styles] = useState(() => mergeToNew(danaOrderUploadInnerStyle, extendedStyles));

    const [uploadedItems, setUploadedItems] = useState<any>([])
    const handleUploadProducts = (uploadedProducts: ProductDto[], onSuccess?: () => void) => {
        if (onUploadProducts) {
            onUploadProducts(uploadedProducts, onSuccess);
            setIsUploading({ isUploading: false });
        }
    };

    const [currentStep, setCurrentStep] = useState<WizardStep>(1);
    const [activeTab, setActiveTab] = useState<ImportTab>("paste");

    const [pasteText, setPasteText] = useState("");

    const [file, setFile] = useState<File | null>(null);
    const [firstRowHeading, setFirstRowHeading] = useState(true);
    const [incorrectExtension, setIncorrectExtension] = useState(false);
    const fileUploadRef = useRef<HTMLInputElement>({ value: "" } as HTMLInputElement);

    const [localInputItems, setLocalInputItems] = useState<InputItem[]>([]);
    const [capturedFirstRowHeading, setCapturedFirstRowHeading] = useState(false);
    const [awaitingFileProcess, setAwaitingFileProcess] = useState(false);
    const [pendingStep2, setPendingStep2] = useState(false);

    const [editableRows, setEditableRows] = useState<EditableRow[]>([]);
    const [showInvalidWarning, setShowInvalidWarning] = useState(false);
    const [showRtbWarning, setShowRtbWarning] = useState(false);
    const [resolvedProducts, setResolvedProducts] = useState<Record<string, ProductDto>>({});
    useEffect(() => {
        setUploadedItems(uploadedListItems)
    }, [uploadedListItems]);

    const handleCloseHandler = () => {
        handleClose?.();
    }

    const resetWizard = useCallback(() => {
        setCurrentStep(1);
        setPasteText("");
        setFile(null);
        setFirstRowHeading(true);
        setIncorrectExtension(false);
        setLocalInputItems([]);
        setAwaitingFileProcess(false);
        setPendingStep2(false);
        setEditableRows([]);
        setShowInvalidWarning(false);
        setShowRtbWarning(false);
        setResolvedProducts({});
        setIsUploading({ isUploading: false });
        setUploadCancelled({ uploadCancelled: false });
        cleanupUploadData();
        if (fileUploadRef.current?.value) {
            fileUploadRef.current.value = "";
        }
    }, [cleanupUploadData, setIsUploading, setUploadCancelled]);


    const buildEditableRows = useCallback(
        (inputItems: InputItem[], headingOffset: boolean): EditableRow[] => {

            let productCursor = 0;
            
            return inputItems.map((item, i) => {
                const rowNumber = headingOffset ? i + 2 : i + 1;
                const error = rowErrors.find(e => e.index === rowNumber) as any;
                const product = products[productCursor++];
                if (error) {
                    const errorNumber = error.error;
                    
                    if (errorNumber === UploadError.WrongType) {

                        const minQty = (product?.minimumOrderQty ?? 0) > 0 ? product!.minimumOrderQty : 1;
                        const packQty = (product?.multipleSaleQty ?? 0) > 1 ? product!.multipleSaleQty : 1;
                        const qty = item.qtyOrdered ?? 1;
                        const hasQtyErr = checkQtyError(qty, minQty, packQty);
                        const hasDateErr = checkDateError(item.requestedShipDate, isEmea);
                        return {
                            id: `row-${rowNumber}`,
                            rowNumber,
                            partNumber: product?.modelNumber ?? item.name,
                            partName: product?.shortDescription ?? "",
                            qty,
                            requestedShipDate: item.requestedShipDate,
                            hasQtyError: hasQtyErr,
                            hasDateError: hasDateErr,
                            needsReview: hasQtyErr || hasDateErr,
                            minimumOrderQty: minQty,
                            multipleSaleQty: packQty,
                            status: hasQtyErr || hasDateErr ? "invalid" : "valid",
                            showSuggestions: false,
                            replacementPartNumber: error?.replacementPartNumber,
                            deleted: false,
                        };
                    }

                    const rtbProduct = !errorNumber ? products[productCursor++] : undefined;

                    return {
                        id: `row-${rowNumber}`,
                        rowNumber,
                        partNumber: item.name,
                        partName: rtbProduct?.name ?? "—",
                        qty: item.qtyOrdered ?? 1,
                        requestedShipDate: item.requestedShipDate,
                        status: errorNumber ? "invalid" : "rtb",
                        errorMessage: errorNumber ? siteMessage(uploadErrorMessages[errorNumber])?.toString() : undefined,
                        replacementPartNumber: error?.replacementPartNumber,
                        needsReview: true,
                        showSuggestions: false,
                        deleted: false,
                    };
                }


                const minQty = (product?.minimumOrderQty ?? 0) > 0 ? product!.minimumOrderQty : 1;
                const packQty = (product?.multipleSaleQty ?? 0) > 1 ? product!.multipleSaleQty : 1;
                const qty = item.qtyOrdered ?? 1;
                const hasQtyErr = checkQtyError(qty, minQty, packQty);
                const hasDateErr = checkDateError(item.requestedShipDate, isEmea);

                return {
                    id: `row-${rowNumber}`,
                    rowNumber,
                    partNumber: product?.modelNumber ?? item.name,
                    partName: product?.shortDescription ?? "",
                    qty,
                    requestedShipDate: item.requestedShipDate,
                    hasQtyError: hasQtyErr,
                    hasDateError: hasDateErr,
                    needsReview: hasQtyErr || hasDateErr,
                    minimumOrderQty: minQty,
                    multipleSaleQty: packQty,
                    status: hasQtyErr || hasDateErr ? "invalid" : "valid",
                    showSuggestions: false,
                    deleted: false,
                };
            });
        },
        [products, rowErrors],
    );

    useEffect(() => {
        if (!awaitingFileProcess || uploadedItems.length === 0) return;
        const items: InputItem[] = uploadedItems.map((u: any) => ({
            name: u.name,
            qtyOrdered: u.qtyOrdered,
            requestedShipDate: u.requestedShipDate,
        }));
        setLocalInputItems(items);
        setAwaitingFileProcess(false);
        setPendingStep2(true);
        batchLoadProducts({
            extendedNames: items.map(i => i.name),
            firstRowHeading: capturedFirstRowHeading,
            checkInventory: false,
        });
    }, [uploadedItems, awaitingFileProcess]);

    useEffect(() => {
        if (!productsProcessed || !pendingStep2) return;
        const rows = buildEditableRows(localInputItems, capturedFirstRowHeading);
        setEditableRows(rows);
        setPendingStep2(false);

        const allClean = rows.every(
            r => r.status === "valid" && !r.hasQtyError && !r.hasDateError,
        );
        if (allClean) {
            const validRows = rows.filter(r => !r.deleted && r.status === "valid");
            const productsToAdd = validRows
                .map(row => {
                    const original = products.find(
                        p => (p.modelNumber ?? "").toLowerCase() === row.partNumber.toLowerCase(),
                    );
                    return original
                        ? { ...original, qtyOrdered: row.qty, properties: { requestedShipDate: row.requestedShipDate } }
                        : null;
                })
                .filter((p): p is any => p !== null);
            if (productsToAdd.length > 0) {
                setAllowCancel({ allowCancel: false });
                handleUploadProducts(productsToAdd, () => setCurrentStep(3));
                setCurrentStep(3);
                return;
            }
        }

        setCurrentStep(2);
    }, [productsProcessed, pendingStep2]);

    useEffect(() => {
        if (isBadFile || uploadLimitExceeded) {
            setAwaitingFileProcess(false);
            setPendingStep2(false);
        }
    }, [isBadFile, uploadLimitExceeded]);


    const handleDownloadTemplate = () => {
        window.open(`${window.location.origin}${templateUrl}`);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const f = event.target.files?.[0] ?? null;
        setFile(f);
        if (f) {
            const ext = getFileExtension(f.name);
            const bad = ["xls", "xlsx", "csv"].indexOf(ext) === -1;
            setIncorrectExtension(bad);
            setIsBadFile({ isBadFile: bad });
            setUploadLimitExceeded({ uploadLimitExceeded: false });
        }
    };

    const handleFirstRowHeadingChange: CheckboxProps["onChange"] = (_, value) => {
        setFirstRowHeading(value);
    };

    const handleCancelUpload = () => {
        setUploadCancelled({ uploadCancelled: true });
        setIsUploading({ isUploading: false });
        cleanupUploadData();
        setFile(null);
        setFirstRowHeading(true);
        setPendingStep2(false);
        setAwaitingFileProcess(false);
        if (fileUploadRef.current?.value) {
            fileUploadRef.current.value = "";
        }
    };

    const handleImportAndValidate = () => {
        cleanupUploadData();
        setUploadedItems([]);
        setLocalInputItems([]);
        setShowInvalidWarning(false);
        setShowRtbWarning(false);
        if (activeTab === "paste") {
            const items = parsePastedText(pasteText);
            if (items.length === 0) return;

            setLocalInputItems(items);
            setCapturedFirstRowHeading(false);
            const csvText = items
                .map(i => [i.name, i.qtyOrdered ?? "", i.requestedShipDate ?? ""].join(","))
                .join("\n");
            const csvBuffer = new TextEncoder().encode(csvText).buffer;
            setAwaitingFileProcess(true);
            processFile({ data: csvBuffer, fileExtension: "csv", firstRowHeading: false });
        } else {
            if (!file || incorrectExtension) return;
            const reader = new FileReader();
            reader.onload = (e: Event) => {
                const data = (e.target as FileReader).result as ArrayBuffer;
                setCapturedFirstRowHeading(firstRowHeading);
                setAwaitingFileProcess(true);
                processFile({
                    data,
                    fileExtension: getFileExtension(file.name),
                    firstRowHeading,
                });
            };
            reader.readAsArrayBuffer(file);
        }
    };


    const updateRowField = (id: string, field: keyof EditableRow, value: unknown) => {
        setEditableRows(prev =>
            prev.map(row => {
                if (row.id !== id) return row;
                const updated = { ...row, [field]: value };
                const hasQtyErr = checkQtyError(updated.qty, updated.minimumOrderQty, updated.multipleSaleQty);
                const hasDateErr = checkDateError(updated?.requestedShipDate, isEmea);
                const status = updated.status === "rtb"
                    ? "rtb"
                    : updated.status === "invalid" && field !== "status" && !hasQtyErr && !hasDateErr && !updated.errorMessage
                        ? "valid"
                        : hasQtyErr || hasDateErr
                            ? "invalid"
                            : updated.status;
                return { ...updated, hasQtyError: hasQtyErr, hasDateError: hasDateErr, status };
            }),
        );
    };

    const deleteRow = (id: string) => {
        setEditableRows(prev =>
            prev.map(row => (row.id === id ? { ...row, deleted: true } : row)),
        );
    };

    const markRowInvalid = (id: string, errorMessage?: string, replacementPart?: ProductDto) => {
        setEditableRows(prev =>
            prev.map(row => {
                if (row.id !== id) return row;
                return {
                    ...row,
                    status: "invalid" as const,
                    partName: "",
                    errorMessage,
                    replacementPart,
                    replacementPartNumber: undefined,
                };
            }),
        );
    };

    const replaceProduct = (rowId: string, product: ProductDto, replacementPart?: ProductDto) => {
        setResolvedProducts(prev => ({ ...prev, [rowId]: product }));
        setEditableRows(prev =>
            prev.map(row => {
                if (row.id !== rowId) return row;
                const minQty = product.minimumOrderQty > 0 ? product.minimumOrderQty : 1;
                const packQty = product.multipleSaleQty > 1 ? product.multipleSaleQty : 1;
                const hasQtyErr = checkQtyError(row.qty, minQty, packQty);
                const hasDateErr = checkDateError(row?.requestedShipDate, isEmea);
                return {
                    ...row,
                    partNumber: product.modelNumber ?? row.partNumber,
                    partName: product.shortDescription ?? "",
                    hasQtyError: hasQtyErr,
                    hasDateError: hasDateErr,
                    minimumOrderQty: minQty,
                    multipleSaleQty: packQty,
                    replacementPart,
                    replacementPartNumber: undefined,
                    status: hasQtyErr || hasDateErr ? "invalid" : "valid",
                    errorMessage: undefined,
                };
            }),
        );
    };

    const handleAddPartsToWishlist = () => {
        const activeRows = editableRows.filter(r => !r.deleted);
        const hasInvalid = activeRows.some(r => r.status === "invalid");
        const hasRtb = activeRows.some(r => r.status === "rtb");

        setShowInvalidWarning(hasInvalid);
        setShowRtbWarning(hasRtb);
        if (hasInvalid || hasRtb) return;

        const validRows = activeRows.filter(r => r.status === "valid");

        const productsToAdd = validRows
            .map(row => {

                const resolved = resolvedProducts[row.id];
                if (resolved) {
                    return { ...resolved, qtyOrdered: row.qty, properties: { requestedShipDate: row.requestedShipDate } };
                }
                const original = products.find(
                    p =>
                        (p.modelNumber ?? "").toLowerCase() === row.partNumber.toLowerCase() ||
                        (p.modelNumber ?? "").toLowerCase() === row.partNumber.toLowerCase(),
                );
                return original ? { ...original, qtyOrdered: row.qty, properties: { requestedShipDate: row.requestedShipDate } } : null;
            })
            .filter((p): p is any => p !== null);

        if (productsToAdd.length === 0) return;

        setAllowCancel({ allowCancel: false });
        handleUploadProducts(productsToAdd, () => setCurrentStep(3));
        setCurrentStep(3);
    };

    const stepState = (n: number): "active" | "done" | "pending" => {
        if (n < currentStep) return "done";
        if (n === currentStep) return "active";
        return "pending";
    };

    const stepCircleContent = (n: number) => (n < currentStep ? "✓" : String(n));

    const activeRows = editableRows.filter(r => !r.deleted);
    const validCount = activeRows.filter(r => r.status === "valid" && !r.hasQtyError && !r.hasDateError).length;
    const problemCount = activeRows.filter(r => r.status !== "valid").length;
    const hasQtyErrors = activeRows.some(r => r.hasQtyError);
    const hasDateErrors = activeRows.some(r => r.hasDateError);

    const importButtonDisabled =
        pendingStep2 ||
        isUploading ||
        (activeTab === "paste" && pasteText.trim().length === 0) ||
        (activeTab === "upload" && (!file || incorrectExtension));


    return (
        <Modal
            isOpen={isOpen}
            {...styles.modal}
            headline={
                <StyledWrapper {...styles.modalHeadingWrapper}>
                    <Typography as="h3" variant="h3">{translate("Upload Items To List")} <Typography>— {wishListName}</Typography></Typography>
                    <StyledWrapper {...styles.stepBarWrapper}>
                        {([1, 2, 3] as const).map((n, idx) => (
                            <React.Fragment key={n}>
                                {idx > 0 && <Typography {...styles.stepConnector} />}
                                <StyledWrapper {...styles.stepBlock}
                                    className={(stepState(n)) === "active" ? "active" : stepState(n) === "done" ? "done" : "pending"}
                                >
                                    <Typography {...styles.stepCircle} className="stepCircle">
                                        {stepState(n) === "done" ? "✓" : stepCircleContent(n)}
                                    </Typography>
                                    <Typography {...styles.stepLabel} className="stepLabel">
                                        {n === 1
                                            ? `1 · ${translate("Import")}`
                                            : n === 2
                                                ? `2 · ${translate("Validate")}`
                                                : `3 · ${translate("Add to Mylist")}`}
                                    </Typography>
                                </StyledWrapper>
                            </React.Fragment>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
            }
            handleClose={handleCloseHandler}
            data-test-selector={`wishListUploadItemsModal_${wishListId}`}
            modalFooter={
                <>
                    {currentStep === 1 && (
                        <>
                            <Button
                                {...styles.backButton}
                                onClick={resetWizard}
                                data-test-selector="danaOrderUpload_cancel"
                            >
                                {translate("Cancel")}
                            </Button>
                            <Button
                                {...styles.nextButton}
                                onClick={handleImportAndValidate}
                                disabled={importButtonDisabled}
                                data-test-selector="danaOrderUpload_importAndValidate"
                            >
                                {pendingStep2 || isUploading ? (
                                    <>
                                        <LoadingSpinner {...styles.spinner} />
                                        <Typography {...styles.uploadingLabel}>
                                            {translate("Validating")}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography {...styles.uploadFileLabel}>
                                        {translate("Validate & Import →")}
                                    </Typography>
                                )}
                            </Button>
                        </>
                    )}

                    {currentStep === 2 && (
                        <>
                            <Button
                                {...styles.backButton}
                                onClick={() => {
                                    setCurrentStep(1);
                                    setPendingStep2(false);
                                    setIsUploading({ isUploading: false });
                                    cleanupUploadData();
                                    setShowInvalidWarning(false);
                                    setShowRtbWarning(false);
                                }}
                                data-test-selector="danaOrderUpload_backToImport"
                            >
                                {translate("← Back to Import")}
                            </Button>
                            <Button
                                {...styles.nextButton}
                                onClick={handleAddPartsToWishlist}
                                disabled={activeRows.length === 0 || activeRows.some(r => r.status === "invalid") || hasQtyErrors || hasDateErrors}
                                data-test-selector="danaOrderUpload_addPartsToWishlist"
                            >
                                <Typography {...styles.uploadFileLabel}>
                                    {translate("Add Parts to Mylist →")}
                                </Typography>
                            </Button>
                        </>
                    )}

                    {currentStep === 3 && <StyledWrapper />}
                </>
            }
        >
            {currentStep === 1 && (
                <>
                    <StyledWrapper {...styles.tabSectionWrapper}>
                        <Button
                            className={activeTab === "paste" ? 'active' : ''}
                            onClick={() => setActiveTab("paste")}
                            {...styles.tabSection}
                        >
                            {translate("Copy & Paste")}
                        </Button>
                        <Button
                            className={activeTab === "upload" ? 'active' : ''}
                            onClick={() => setActiveTab("upload")}
                            {...styles.tabSection}
                        >
                            {translate("Upload Excel / CSV File")}
                        </Button>
                    </StyledWrapper>

                    {activeTab === "paste" && (
                        <DanaOrderUploadStep1Paste
                            pasteText={pasteText}
                            onChange={setPasteText}
                            isEmea={isEmea}
                        />
                    )}

                    {activeTab === "upload" && (
                        <DanaOrderUploadStep1File
                            file={file}
                            firstRowHeading={firstRowHeading}
                            incorrectExtension={incorrectExtension}
                            isBadFile={isBadFile}
                            uploadLimitExceeded={uploadLimitExceeded}
                            fileUploadRef={fileUploadRef}
                            extendedStyles={styles}
                            onDownloadTemplate={handleDownloadTemplate}
                            onFileChange={handleFileChange}
                            onFirstRowHeadingChange={handleFirstRowHeadingChange}
                            onCancelUpload={handleCancelUpload}
                        />
                    )}
                </>
            )}

            {currentStep === 2 && (
                <DanaOrderUploadStep2Validate
                    rows={editableRows}
                    validCount={validCount}
                    problemCount={problemCount}
                    showInvalidWarning={showInvalidWarning}
                    showRtbWarning={showRtbWarning}
                    isEmea={isEmea}
                    onUpdateField={updateRowField}
                    onMarkInvalid={markRowInvalid}
                    onDeleteRow={deleteRow}
                    onReplaceProduct={replaceProduct}
                />
            )}


            {currentStep === 3 && (
                <DanaOrderUploadStep3Confirm
                    validCount={validCount}
                    onUploadMore={resetWizard}
                    onDone={() => { resetWizard(); handleClose?.(); }}
                    wishListName={wishListName}
                />
            )}
        </Modal>
    );
};

const DanaOrderUpload = connect(mapStateToProps, mapDispatchToProps)(DanaOrderUploadInner);
export default DanaOrderUpload;
